import { useState } from 'react'
import { X, MessageCircle, Mail, FileText, Bug, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { trpc } from '../providers/trpc'
import { useFocusTrap } from '../hooks/useFocusTrap'

interface SupportModalProps {
  isOpen: boolean
  onClose: () => void
}

type SupportTab = 'contact' | 'faq' | 'bug'

export default function SupportModal({ isOpen, onClose }: SupportModalProps) {
  const [activeTab, setActiveTab] = useState<SupportTab>('contact')
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    priority: 'medium',
    browser: '',
    steps: '',
  })

  const createTicket = trpc.ticket.create.useMutation({
    onSuccess: (data) => {
      toast.success(`Ticket #${data.ticketId} submitted! We will respond within 24 hours.`)
      setFormData({ name: '', email: '', subject: '', message: '', priority: 'medium', browser: '', steps: '' })
      onClose()
    },
    onError: (err) => {
      toast.error(err.message || 'Failed to submit. Please try again.')
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!formData.name.trim() || !formData.email.trim() || !formData.subject.trim() || !formData.message.trim()) {
      toast.error('Please fill in all required fields.')
      return
    }
    const category = activeTab === 'bug' ? 'bug' : 'other'
    const body = activeTab === 'bug'
      ? `Bug Report:

Description:
${formData.message}

Steps to Reproduce:
${formData.steps || 'Not provided'}

Browser & OS:
${formData.browser || 'Not provided'}`
      : formData.message
    createTicket.mutate({
      email: formData.email.trim(),
      name: formData.name.trim(),
      subject: formData.subject.trim(),
      body,
      category,
    })
  }

  const trapRef = useFocusTrap(isOpen, { onEscape: onClose, returnFocusOnDeactivate: true })

  if (!isOpen) return null

  const faqs = [
    { q: 'How do I export parcel data?', a: 'Go to your dashboard, select parcels using checkboxes, then click Export CSV or Export PDF in the toolbar.' },
    { q: 'Can I change my commission rate?', a: 'Yes! Go to Dashboard → Commission Settings to adjust your default rate and split percentage.' },
    { q: 'How often is parcel data updated?', a: 'County data refreshes weekly. Premium plans get daily updates for watched counties.' },
    { q: 'What is the Investment Score?', a: 'It rates parcels 0-100 based on power grid access, transmission lines, road access, elevation, and flood risk.' },
    { q: 'How do I invite team members?', a: 'Go to Dashboard → Team. Enter their email and select a role (Admin, Agent, or Viewer).' },
  ]

  const tabs = [
    { id: 'contact' as SupportTab, label: 'Contact', icon: MessageCircle },
    { id: 'faq' as SupportTab, label: 'FAQ', icon: FileText },
    { id: 'bug' as SupportTab, label: 'Report Bug', icon: Bug },
  ]

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" style={{ background: 'rgba(10,15,20,0.85)', backdropFilter: 'blur(8px)' }}>
      <div
        ref={trapRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="support-modal-title"
        className="relative w-full max-w-lg rounded-xl border overflow-hidden"
        style={{ background: '#232F3E', borderColor: 'rgba(247,248,244,0.08)' }}
      >
        <div className="flex items-center justify-between p-5 border-b" style={{ borderColor: 'rgba(247,248,244,0.08)' }}>
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-lg flex items-center justify-center" style={{ background: 'rgba(247,248,244,0.08)' }}>
              <MessageCircle size={18} style={{ color: '#F7F8F4' }} />
            </div>
            <div>
              <h3 id="support-modal-title" className="text-base font-semibold" style={{ color: '#F7F8F4' }}>Customer Support</h3>
              <p className="text-xs" style={{ color: '#A8B8AE' }}>We typically respond within 24 hours</p>
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Close support dialog"
            className="h-8 w-8 rounded-lg flex items-center justify-center transition-colors"
            style={{ color: '#A8B8AE' }}
            onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(247,248,244,0.08)')}
            onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
          >
            <X size={16} />
          </button>
        </div>
        <div className="flex border-b" style={{ borderColor: 'rgba(247,248,244,0.08)' }}>
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className="flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium transition-all border-b-2"
              style={{
                color: activeTab === tab.id ? '#F7F8F4' : '#A8B8AE',
                borderColor: activeTab === tab.id ? '#F7F8F4' : 'transparent',
              }}
            >
              <tab.icon size={15} />
              {tab.label}
            </button>
          ))}
        </div>
        <div className="p-5 max-h-[60vh] overflow-y-auto custom-scrollbar">
          {activeTab === 'contact' && (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium mb-1.5" style={{ color: 'rgba(245,245,243,0.7)' }}>Name</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full rounded-lg border px-3 py-2 text-sm outline-none transition-colors focus:border-white"
                    style={{ background: '#07110D', borderColor: 'rgba(247,248,244,0.12)', color: '#F7F8F4' }}
                    placeholder="Your name"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1.5" style={{ color: 'rgba(245,245,243,0.7)' }}>Email</label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full rounded-lg border px-3 py-2 text-sm outline-none transition-colors focus:border-white"
                    style={{ background: '#07110D', borderColor: 'rgba(247,248,244,0.12)', color: '#F7F8F4' }}
                    placeholder="you@company.com"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium mb-1.5" style={{ color: 'rgba(245,245,243,0.7)' }}>Subject</label>
                <input
                  type="text"
                  required
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  className="w-full rounded-lg border px-3 py-2 text-sm outline-none transition-colors focus:border-white"
                  style={{ background: '#07110D', borderColor: 'rgba(247,248,244,0.12)', color: '#F7F8F4' }}
                  placeholder="What do you need help with?"
                />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1.5" style={{ color: 'rgba(245,245,243,0.7)' }}>Message</label>
                <textarea
                  required
                  rows={4}
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  className="w-full rounded-lg border px-3 py-2 text-sm outline-none transition-colors resize-none focus:border-white"
                  style={{ background: '#07110D', borderColor: 'rgba(247,248,244,0.12)', color: '#F7F8F4' }}
                  placeholder="Describe your issue in detail..."
                />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1.5" style={{ color: 'rgba(245,245,243,0.7)' }}>Priority</label>
                <div className="flex gap-2">
                  {['low', 'medium', 'high'].map((p) => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => setFormData({ ...formData, priority: p })}
                      className="flex-1 py-2 rounded-lg text-xs font-medium capitalize transition-all border"
                      style={{
                        background: formData.priority === p ? 'rgba(247,248,244,0.08)' : '#07110D',
                        borderColor: formData.priority === p ? 'rgba(255,255,255,0.3)' : 'rgba(247,248,244,0.12)',
                        color: formData.priority === p ? '#F7F8F4' : '#A8B8AE',
                      }}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>
              <button
                type="submit"
                disabled={createTicket.isPending}
                className="w-full py-2.5 rounded-lg text-sm font-semibold transition-all hover:opacity-90 flex items-center justify-center gap-2"
                style={{ background: '#F7F8F4', color: '#07110D' }}
              >
                {createTicket.isPending ? <Loader2 size={16} className="animate-spin" /> : null}
                Submit Support Request
              </button>
              <div className="flex items-center justify-center gap-4 pt-2">
                <a href="mailto:support@parcleadpro.com" className="flex items-center gap-1.5 text-xs transition-colors hover:opacity-80" style={{ color: '#A8B8AE' }}>
                  <Mail size={12} /> support@parcleadpro.com
                </a>
              </div>
            </form>
          )}
          {activeTab === 'faq' && (
            <div className="space-y-3">
              {faqs.map((faq, i) => (
                <div key={i} className="rounded-lg border p-4" style={{ background: '#07110D', borderColor: 'rgba(255,255,255,0.06)' }}>
                  <p className="text-sm font-medium mb-1.5" style={{ color: '#F7F8F4' }}>{faq.q}</p>
                  <p className="text-xs leading-relaxed" style={{ color: '#A8B8AE' }}>{faq.a}</p>
                </div>
              ))}
            </div>
          )}
          {activeTab === 'bug' && (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-medium mb-1.5" style={{ color: 'rgba(245,245,243,0.7)' }}>Bug Summary *</label>
                <input
                  type="text"
                  required
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  className="w-full rounded-lg border px-3 py-2 text-sm outline-none transition-colors focus:border-white"
                  style={{ background: '#07110D', borderColor: 'rgba(247,248,244,0.12)', color: '#F7F8F4' }}
                  placeholder="Short summary of the bug"
                />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1.5" style={{ color: 'rgba(245,245,243,0.7)' }}>Bug Description *</label>
                <textarea
                  required
                  rows={3}
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  className="w-full rounded-lg border px-3 py-2 text-sm outline-none transition-colors resize-none focus:border-white"
                  style={{ background: '#07110D', borderColor: 'rgba(247,248,244,0.12)', color: '#F7F8F4' }}
                  placeholder="What went wrong?"
                />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1.5" style={{ color: 'rgba(245,245,243,0.7)' }}>Steps to Reproduce</label>
                <textarea
                  rows={3}
                  value={formData.steps}
                  onChange={(e) => setFormData({ ...formData, steps: e.target.value })}
                  className="w-full rounded-lg border px-3 py-2 text-sm outline-none transition-colors resize-none focus:border-white"
                  style={{ background: '#07110D', borderColor: 'rgba(247,248,244,0.12)', color: '#F7F8F4' }}
                  placeholder="1. Go to... 2. Click... 3. See error..."
                />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1.5" style={{ color: 'rgba(245,245,243,0.7)' }}>Browser &amp; OS</label>
                <input
                  type="text"
                  value={formData.browser}
                  onChange={(e) => setFormData({ ...formData, browser: e.target.value })}
                  className="w-full rounded-lg border px-3 py-2 text-sm outline-none transition-colors focus:border-white"
                  style={{ background: '#07110D', borderColor: 'rgba(247,248,244,0.12)', color: '#F7F8F4' }}
                  placeholder="e.g. Chrome 120 / macOS Sonoma"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium mb-1.5" style={{ color: 'rgba(245,245,243,0.7)' }}>Name</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full rounded-lg border px-3 py-2 text-sm outline-none transition-colors focus:border-white"
                    style={{ background: '#07110D', borderColor: 'rgba(247,248,244,0.12)', color: '#F7F8F4' }}
                    placeholder="Your name"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1.5" style={{ color: 'rgba(245,245,243,0.7)' }}>Email</label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full rounded-lg border px-3 py-2 text-sm outline-none transition-colors focus:border-white"
                    style={{ background: '#07110D', borderColor: 'rgba(247,248,244,0.12)', color: '#F7F8F4' }}
                    placeholder="you@company.com"
                  />
                </div>
              </div>
              <button
                type="submit"
                disabled={createTicket.isPending}
                className="w-full py-2.5 rounded-lg text-sm font-semibold transition-all hover:opacity-90 flex items-center justify-center gap-2"
                style={{ background: '#F7F8F4', color: '#07110D' }}
              >
                {createTicket.isPending ? <Loader2 size={16} className="animate-spin" /> : null}
                Submit Bug Report
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  )
}
